/**
 * マスターデータAPI（統合版）
 * 職人、車両、材料、社用車のマスターデータ管理
 */

import apiClient from '@/shared/api/client'
import type {
    Craftsman, CraftsmanInsert, CraftsmanUpdate,
    Truck, TruckInsert, TruckUpdate,
    Material, MaterialInsert, MaterialUpdate,
    CompanyVehicle, CompanyVehicleInsert, CompanyVehicleUpdate,
    TruckDriver, TruckDriverInsert, TruckDriverUpdate
} from '@/features/masters/types'

/**
 * マスターデータAPI
 */
export const mastersApi = {
    /**
     * 職人関連
     */
    craftsmen: {
        /**
         * 全職人取得
         */
        getAll: async (): Promise<Craftsman[]> => {
            const response = await apiClient.get('/masters/craftsmen')
            return response.data.items || []
        },

        /**
         * ID指定で職人取得
         */
        getById: async (id: string): Promise<Craftsman> => {
            const response = await apiClient.get(`/masters/craftsmen/${id}`)
            return response.data
        },

        /**
         * 職人作成
         */
        create: async (data: CraftsmanInsert): Promise<Craftsman> => {
            const response = await apiClient.post('/masters/craftsmen', data)
            return response.data
        },

        /**
         * 職人更新
         */
        update: async (id: string, data: CraftsmanUpdate): Promise<Craftsman> => {
            const response = await apiClient.patch(`/masters/craftsmen/${id}`, data)
            return response.data
        },

        /**
         * 職人削除
         */
        delete: async (id: string): Promise<void> => {
            await apiClient.delete(`/masters/craftsmen/${id}`)
        },
    },

    /**
     * 社用車関連
     */
    companyVehicles: {
        /**
         * 全社用車取得
         */
        getAll: async (): Promise<CompanyVehicle[]> => {
            const response = await apiClient.get('/masters/company-vehicles')
            return response.data.items || []
        },
        /**
         * ID指定で社用車取得
         */
        getById: async (id: string): Promise<CompanyVehicle> => {
            const response = await apiClient.get(`/masters/company-vehicles/${id}`)
            return response.data
        },
        /**
         * 社用車作成
         */
        create: async (data: CompanyVehicleInsert): Promise<CompanyVehicle> => {
            const response = await apiClient.post('/masters/company-vehicles', data)
            return response.data
        },
        /**
         * 社用車更新
         */
        update: async (id: string, data: CompanyVehicleUpdate): Promise<CompanyVehicle> => {
            const response = await apiClient.put(`/masters/company-vehicles/${id}`, data)
            return response.data
        },
        /**
         * 社用車削除
         */
        delete: async (id: string): Promise<void> => {
            await apiClient.delete(`/masters/company-vehicles/${id}`)
        },
    },

    /**
     * 車両関連
     */
    trucks: {
        /**
         * 全車両取得
         */
        getAll: async (): Promise<Truck[]> => {
            const response = await apiClient.get('/masters/trucks')
            return response.data.items || []
        },

        /**
         * ID指定で車両取得
         */
        getById: async (id: string): Promise<Truck> => {
            const response = await apiClient.get(`/masters/trucks/${id}`)
            return response.data
        },

        /**
         * 車両作成
         */
        create: async (data: TruckInsert): Promise<Truck> => {
            const response = await apiClient.post('/masters/trucks', data)
            return response.data
        },

        /**
         * 車両更新
         */
        update: async (id: string, data: TruckUpdate): Promise<Truck> => {
            const response = await apiClient.patch(`/masters/trucks/${id}`, data)
            return response.data
        },

        /**
         * 車両削除
         */
        delete: async (id: string): Promise<void> => {
            await apiClient.delete(`/masters/trucks/${id}`)
        },
    },

    /**
     * トラック運転手関連
     */
    truckDrivers: {
        /**
         * 全運転手取得
         */
        getAll: async (): Promise<TruckDriver[]> => {
            const response = await apiClient.get('/masters/truck-drivers')
            return response.data.items || []
        },
        /**
         * ID指定で運転手取得
         */
        getById: async (id: string): Promise<TruckDriver> => {
            const response = await apiClient.get(`/masters/truck-drivers/${id}`)
            return response.data
        },
        /**
         * 運転手作成
         */
        create: async (data: TruckDriverInsert): Promise<TruckDriver> => {
            const response = await apiClient.post('/masters/truck-drivers', data)
            return response.data
        },
        /**
         * 運転手更新
         */
        update: async (id: string, data: TruckDriverUpdate): Promise<TruckDriver> => {
            const response = await apiClient.put(`/masters/truck-drivers/${id}`, data)
            return response.data
        },
        /**
         * 運転手削除
         */
        delete: async (id: string): Promise<void> => {
            await apiClient.delete(`/masters/truck-drivers/${id}`)
        },
    },

    /**
     * 材料関連
     */
    materials: {
        /**
         * 全材料取得
         */
        getAll: async (): Promise<Material[]> => {
            const response = await apiClient.get('/masters/materials')
            return response.data
        },

        /**
         * ID指定で材料取得
         */
        getById: async (id: string): Promise<Material> => {
            const response = await apiClient.get(`/masters/materials/${id}`)
            return response.data
        },

        /**
         * 材料作成
         */
        create: async (data: MaterialInsert): Promise<Material> => {
            const response = await apiClient.post('/masters/materials', data)
            return response.data
        },

        /**
         * 材料更新
         */
        update: async (id: string, data: MaterialUpdate): Promise<Material> => {
            const response = await apiClient.patch(`/masters/materials/${id}`, data)
            return response.data
        },

        /**
         * 材料削除
         */
        delete: async (id: string): Promise<void> => {
            await apiClient.delete(`/masters/materials/${id}`)
        },
    },
}
